// SystemScreen — Phase 5 a.7 Health-page surface.
//
// Two panels:
//   1. Wizard CTA (carried forward from the prior StubScreen so
//      the operator's existing flow keeps working).
//   2. "Recent backup tests" panel + "Verify latest backup" button
//      per UX §14.1.
//
// The backup panel manages its own data fetch (independent of the
// dashboard's useDataSource hook) because backup state is read on
// the system page only, and folding it into the dashboard hook
// would cost a fetch on every dashboard render.
//
// Phase 5 (a) scope: this screen exposes the verify path. The
// "run a full restore test now" button from the spec is honest
// about scope — the full test takes hours per RTO and runs on
// the weekly Sunday scheduler tick (a.5). The verify button gives
// the operator immediate feedback that the most recent backup is
// verifiable without restoring; it's the next-best on-demand
// signal.

import { useCallback, useEffect, useState } from "react";
import {
  createDataSource,
  type DataSource,
} from "../api/dataSource";
import type {
  AiCostStatusWire,
  BackupTestOutcomeWire,
  BackupVerifyResponseWire,
  HealthResponse,
  SubsystemStatusWire,
} from "../api/client";

type Props = {
  /** CTA rendered above the backup panel (the wizard button). */
  cta?: React.ReactNode;
};

export const SystemScreen = ({ cta }: Props) => {
  const [source, setSource] = useState<DataSource | null>(null);
  const [outcomes, setOutcomes] = useState<BackupTestOutcomeWire[]>([]);
  const [outcomesLoading, setOutcomesLoading] = useState<boolean>(true);
  const [outcomesError, setOutcomesError] = useState<string | null>(null);

  const [verifyResult, setVerifyResult] = useState<
    BackupVerifyResponseWire | null
  >(null);
  const [verifying, setVerifying] = useState<boolean>(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // Phase 5 (b) b.2: subsystem dots from /health.
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);

  // Phase 7a D8: monthly AI cost status.
  const [aiCost, setAiCost] = useState<AiCostStatusWire | null>(null);

  // Resolve the data source once on mount.
  useEffect(() => {
    let cancelled = false;
    void createDataSource().then((ds) => {
      if (!cancelled) setSource(ds);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshOutcomes = useCallback(async () => {
    if (source === null) return;
    setOutcomesError(null);
    setOutcomesLoading(true);
    try {
      const r = await source.listBackupTests(7);
      setOutcomes(r.outcomes);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[SystemScreen] listBackupTests failed", e);
      setOutcomesError(
        e instanceof Error ? e.message : "Failed to load backup tests",
      );
    } finally {
      setOutcomesLoading(false);
    }
  }, [source]);

  useEffect(() => {
    if (source !== null) void refreshOutcomes();
  }, [source, refreshOutcomes]);

  const refreshHealth = useCallback(async () => {
    if (source === null) return;
    setHealthError(null);
    try {
      const r = await source.getHealth();
      setHealth(r);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[SystemScreen] getHealth failed", e);
      setHealthError(
        e instanceof Error ? e.message : "Failed to load health",
      );
    }
  }, [source]);

  useEffect(() => {
    if (source !== null) void refreshHealth();
  }, [source, refreshHealth]);

  const refreshAiCost = useCallback(async () => {
    if (source === null) return;
    try {
      const r = await source.getAiCostStatus();
      setAiCost(r);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[SystemScreen] getAiCostStatus failed", e);
    }
  }, [source]);

  useEffect(() => {
    if (source !== null) void refreshAiCost();
  }, [source, refreshAiCost]);

  const handleVerify = useCallback(async () => {
    if (source === null) return;
    setVerifying(true);
    setVerifyError(null);
    try {
      const r = await source.verifyLatestBackup();
      setVerifyResult(r);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[SystemScreen] verifyLatestBackup failed", e);
      setVerifyError(
        e instanceof Error ? e.message : "Verify request failed",
      );
    } finally {
      setVerifying(false);
    }
  }, [source]);

  return (
    <div data-screen-label="System">
      <div className="tp-page-head">
        <h1>System</h1>
        <div className="meta">backup integrity, AI state, reconciliation</div>
      </div>

      {cta && <div className="tp-tile" style={{ marginBottom: 14 }}>
        <div className="tp-tile-body">{cta}</div>
      </div>}

      {/* ----- AI cost ceiling banner (non-dismissable when near ceiling) ----- */}
      {aiCost?.near_ceiling && (
        <div
          role="alert"
          style={{
            background: "var(--tp-warn, #7a5800)",
            color: "#fff",
            padding: "10px 16px",
            borderRadius: 6,
            marginBottom: 14,
            fontWeight: 500,
          }}
        >
          AI cost approaching monthly ceiling — ${aiCost.estimated_cost_usd} of
          ${aiCost.ceiling_usd} used this month. Adjust ceiling in system_config
          if needed.
        </div>
      )}

      {/* ----- AI cost widget (Phase 7a D8) ----- */}
      <div className="tp-tile" style={{ marginBottom: 14 }}>
        <div className="tp-tile-head">
          <div className="tp-tile-title">AI cost this month</div>
          <div className="tp-tile-meta">
            {aiCost
              ? `${aiCost.monthly_calls} scoring call${aiCost.monthly_calls === 1 ? "" : "s"}`
              : "loading…"}
          </div>
        </div>
        <div className="tp-tile-body">
          {aiCost === null ? (
            <div style={{ color: "var(--tp-fg-3)" }}>loading…</div>
          ) : (
            <AiCostWidget status={aiCost} />
          )}
        </div>
      </div>

      {/* ----- Subsystem status (UX §14.1, Phase 5 b.2) ----- */}
      <div className="tp-tile" style={{ marginBottom: 14 }}>
        <div className="tp-tile-head">
          <div className="tp-tile-title">Subsystems</div>
          <div className="tp-tile-meta">
            {health
              ? `aggregate: ${health.status.toUpperCase()}`
              : "loading…"}
          </div>
        </div>
        <div className="tp-tile-body">
          {healthError ? (
            <div style={{ color: "var(--tp-bad)" }}>
              error: {healthError}
            </div>
          ) : health === null ? (
            <div style={{ color: "var(--tp-fg-3)" }}>loading…</div>
          ) : (
            <SubsystemList subsystems={health.subsystems} />
          )}
        </div>
      </div>

      {/* ----- Recent backup tests panel (UX §14.1) ----- */}
      <div className="tp-tile">
        <div className="tp-tile-head">
          <div className="tp-tile-title">Recent backup tests</div>
          <div className="tp-tile-meta">
            weekly automated · Sunday 03:00 ET
          </div>
        </div>
        <div className="tp-tile-body">
          {outcomesLoading ? (
            <div style={{ color: "var(--tp-fg-3)" }}>loading…</div>
          ) : outcomesError ? (
            <div style={{ color: "var(--tp-bad)" }}>
              error: {outcomesError}
            </div>
          ) : outcomes.length === 0 ? (
            <div style={{ color: "var(--tp-fg-3)" }}>
              No restore tests have run yet. The first one will appear
              here after the weekly Sunday tick.
            </div>
          ) : (
            <BackupTestsTable outcomes={outcomes} />
          )}
        </div>
      </div>

      {/* ----- Verify latest backup button ----- */}
      <div className="tp-tile" style={{ marginTop: 14 }}>
        <div className="tp-tile-head">
          <div className="tp-tile-title">Verify latest backup</div>
          <div className="tp-tile-meta">on-demand · sha256 + sidecars</div>
        </div>
        <div className="tp-tile-body">
          <p style={{ color: "var(--tp-fg-3)", marginTop: 0 }}>
            Verifies the most recent backup file&apos;s integrity
            (sha256, .chainhead, .rowcounts). Does <strong>not</strong>
            run a full restore — that&apos;s the weekly automated
            test above.
          </p>
          <button
            type="button"
            className="tp-btn tp-btn-secondary"
            onClick={() => void handleVerify()}
            disabled={verifying}
          >
            {verifying ? "Verifying…" : "Verify latest backup"}
          </button>
          {verifyError && (
            <div
              style={{ color: "var(--tp-bad)", marginTop: 10 }}
            >
              error: {verifyError}
            </div>
          )}
          {verifyResult && (
            <VerifyResult result={verifyResult} />
          )}
        </div>
      </div>
    </div>
  );
};

const BackupTestsTable = ({
  outcomes,
}: {
  outcomes: BackupTestOutcomeWire[];
}) => {
  return (
    <table
      data-testid="backup-tests-table"
      style={{ width: "100%", borderCollapse: "collapse" }}
    >
      <thead>
        <tr style={{ textAlign: "left", color: "var(--tp-fg-3)" }}>
          <th style={{ padding: "6px 8px" }}>When</th>
          <th style={{ padding: "6px 8px" }}>Outcome</th>
          <th style={{ padding: "6px 8px" }}>Duration</th>
          <th style={{ padding: "6px 8px" }}>Failed criteria</th>
        </tr>
      </thead>
      <tbody>
        {outcomes.map((o, i) => (
          <tr
            key={`${o.occurred_at}-${i}`}
            style={{ borderTop: "1px solid var(--tp-border)" }}
          >
            <td style={{ padding: "6px 8px" }}>
              {new Date(o.occurred_at).toLocaleString()}
            </td>
            <td
              style={{
                padding: "6px 8px",
                color:
                  o.event_type === "restore_test_passed"
                    ? "var(--tp-good)"
                    : "var(--tp-bad)",
              }}
            >
              {o.event_type === "restore_test_passed" ? "PASS" : "FAIL"}
            </td>
            <td style={{ padding: "6px 8px" }}>
              {o.duration_seconds !== null
                ? `${o.duration_seconds.toFixed(1)}s`
                : "—"}
            </td>
            <td
              style={{
                padding: "6px 8px",
                color: "var(--tp-fg-3)",
                fontSize: 12,
              }}
            >
              {o.failed_criteria !== null && o.failed_criteria.length > 0
                ? o.failed_criteria.join(", ")
                : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const VerifyResult = ({
  result,
}: {
  result: BackupVerifyResponseWire;
}) => {
  const color =
    result.status === "passed"
      ? "var(--tp-good)"
      : result.status === "no_backup"
        ? "var(--tp-fg-3)"
        : "var(--tp-bad)";
  return (
    <div
      data-testid="verify-result"
      style={{ marginTop: 12 }}
    >
      <div style={{ color, fontWeight: 600 }}>
        Status: {result.status.toUpperCase()}
      </div>
      {result.backup_path && (
        <div
          style={{
            color: "var(--tp-fg-3)",
            fontSize: 12,
            marginTop: 4,
          }}
        >
          file: {result.backup_path}
        </div>
      )}
      {result.checks.length > 0 && (
        <ul style={{ marginTop: 8, fontSize: 12, paddingLeft: 18 }}>
          {result.checks.map((c, i) => (
            <li
              key={`${c.name}-${i}`}
              style={{
                color:
                  c.status === "passed"
                    ? "var(--tp-fg-1)"
                    : "var(--tp-bad)",
              }}
            >
              <strong>{c.name}</strong>: {c.status} — {c.detail}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AiCostWidget = ({ status }: { status: AiCostStatusWire }) => {
  const pct =
    parseFloat(status.ceiling_usd) > 0
      ? Math.min(
          100,
          Math.round(
            (parseFloat(status.estimated_cost_usd) /
              parseFloat(status.ceiling_usd)) *
              100,
          ),
        )
      : 0;
  const barColor = status.near_ceiling
    ? "var(--tp-bad)"
    : "var(--tp-good)";
  return (
    <div data-testid="ai-cost-widget">
      <div
        style={{
          display: "flex",
          gap: 16,
          fontSize: 14,
          marginBottom: 10,
        }}
      >
        <span>
          <strong>${status.estimated_cost_usd}</strong>
          {" / "}${status.ceiling_usd} ceiling
        </span>
        <span style={{ color: "var(--tp-fg-3)" }}>
          provider: {status.news_provider_name}
        </span>
      </div>
      <div
        style={{
          height: 8,
          background: "var(--tp-border)",
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: barColor,
            transition: "width 0.3s",
          }}
        />
      </div>
    </div>
  );
};

const SubsystemList = ({
  subsystems,
}: {
  subsystems: SubsystemStatusWire[];
}) => {
  if (subsystems.length === 0) {
    return (
      <div style={{ color: "var(--tp-fg-3)" }}>
        Subsystem reporting not yet wired in this environment.
        (/health returns the legacy unconditional 'ok' shape.)
      </div>
    );
  }
  return (
    <div
      data-testid="subsystem-list"
      style={{ display: "grid", gap: 6 }}
    >
      {subsystems.map((s) => {
        const dotColor =
          s.status === "ok"
            ? "var(--tp-good)"
            : s.status === "degraded"
              ? "var(--tp-warn, #c89a00)"
              : "var(--tp-bad)";
        return (
          <div
            key={s.name}
            style={{
              display: "grid",
              gridTemplateColumns: "12px 160px 1fr",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span
              aria-label={s.status}
              style={{
                width: 10,
                height: 10,
                borderRadius: 10,
                backgroundColor: dotColor,
              }}
            />
            <span style={{ fontWeight: 500 }}>{s.name}</span>
            <span
              style={{ color: "var(--tp-fg-3)", fontSize: 12 }}
            >
              {s.detail}
            </span>
          </div>
        );
      })}
    </div>
  );
};
